import React, { useState, useEffect, useMemo } from 'react';
import {
  ChevronRight,
  ChevronLeft,
  Check,
  AlertCircle,
  FileText,
  User,
  Shield,
  Send,
  Eye,
  Loader2,
  Calendar,
  CreditCard,
} from 'lucide-react';
import { generateTE9Pdf, generateTE7Pdf } from '../utils/teFormPdf';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export type TeFormType = 'te9' | 'te7';

export type Title = 'Mr' | 'Mrs' | 'Miss' | 'Ms' | 'Other';

export type GroundType =
  | 'did_not_receive_pcn'
  | 'made_representations_no_reply'
  | 'appealed_no_response'
  | 'appealed_not_determined'
  | 'appealed_in_favour'
  | 'penalty_paid';

export type SigningCapacity = 'witness' | 'officer' | 'partner' | 'litigation_friend';

export interface PaymentDetails {
  datePaid: string;
  method: 'cash' | 'cheque' | 'debit' | 'credit';
  paidTo: string;
}

export interface TeFormData {
  // Case details (from TE3/Order for Recovery)
  penaltyChargeNumber: string;
  vehicleRegistration: string;
  applicantName: string;
  locationOfContravention: string;
  dateOfContravention: string;

  // Personal details
  title: Title;
  otherTitle?: string;
  fullName: string;
  companyName?: string;
  addressLine1: string;
  addressLine2?: string;
  addressLine3?: string;
  postcode: string;

  // Grounds for TE9 (Witness Statement)
  grounds: GroundType[];
  paymentDetails?: PaymentDetails;

  // Reason for TE7 (Out of Time)
  outOfTimeReason?: string;

  // Statement of truth
  statementOfTruthConfirmed: boolean;
  signingCapacity: SigningCapacity;
  signatureDate: string;
  printedName: string;
  signatureDataUrl?: string;
}

export interface TeFormWizardProps {
  formType: TeFormType;
  includeOutOfTime?: boolean;
  initialData?: Partial<TeFormData>;
  onSubmit: (data: TeFormData, pdfBytes: { te9?: Uint8Array; te7?: Uint8Array }) => void;
  onCancel: () => void;
  ticket?: {
    pcn_number?: string;
    vrn?: string;
    location?: string;
    issue_date?: string;
    contravention_date?: string;
    issuing_authority?: string;
    assigned_driver_name?: string;
  };
  organization?: {
    name?: string;
    address_line1?: string;
    address_line2?: string;
    city?: string;
    postcode?: string;
  };
  driver?: {
    full_name?: string;
    address_line1?: string;
    address_line2?: string;
    city?: string;
    postcode?: string;
  };
}

// ============================================================================
// VALIDATION
// ============================================================================

interface ValidationErrors {
  [key: string]: string;
}

const validateStep = (step: number, data: Partial<TeFormData>, formType: TeFormType, includeOutOfTime: boolean): ValidationErrors => {
  const errors: ValidationErrors = {};

  switch (step) {
    case 0: // Case Details
      if (!data.penaltyChargeNumber?.trim()) errors.penaltyChargeNumber = 'Penalty Charge Number is required';
      if (!data.vehicleRegistration?.trim()) errors.vehicleRegistration = 'Vehicle Registration is required';
      if (!data.dateOfContravention?.trim()) errors.dateOfContravention = 'Date of Contravention is required';
      break;

    case 1: // Personal Details
      if (!data.title) errors.title = 'Title is required';
      if (data.title === 'Other' && !data.otherTitle?.trim()) errors.otherTitle = 'Please specify title';
      if (!data.fullName?.trim()) errors.fullName = 'Full name is required';
      if (!data.addressLine1?.trim()) errors.addressLine1 = 'Address is required';
      if (!data.postcode?.trim()) errors.postcode = 'Postcode is required';
      break;

    case 2: // Grounds (TE9 only)
      if (formType === 'te9') {
        if (!data.grounds?.length) errors.grounds = 'Select at least one ground';
        if (data.grounds?.includes('penalty_paid')) {
          if (!data.paymentDetails?.datePaid) errors.paymentDatePaid = 'Date paid is required';
          if (!data.paymentDetails?.method) errors.paymentMethod = 'Payment method is required';
          if (!data.paymentDetails?.paidTo?.trim()) errors.paymentPaidTo = 'Who was it paid to is required';
        }
      }
      break;

    case 3: // Out of Time Reason (TE7 only, if included)
      if (includeOutOfTime && !data.outOfTimeReason?.trim()) {
        errors.outOfTimeReason = 'Please explain why you are filing out of time';
      }
      break;

    case 4: // Statement of Truth
      if (!data.statementOfTruthConfirmed) errors.statementOfTruthConfirmed = 'You must confirm the statement of truth';
      if (!data.signingCapacity) errors.signingCapacity = 'Signing capacity is required';
      if (!data.signatureDate) errors.signatureDate = 'Date is required';
      if (!data.printedName?.trim()) errors.printedName = 'Printed name is required';
      if (!data.signatureDataUrl) errors.signatureDataUrl = 'Signature is required';
      break;
  }

  return errors;
};

// ============================================================================
// FORM SECTIONS
// ============================================================================

interface StepProps {
  data: Partial<TeFormData>;
  onChange: (updates: Partial<TeFormData>) => void;
  errors: ValidationErrors;
  formType: TeFormType;
}

const CaseDetailsStep: React.FC<StepProps> = ({ data, onChange, errors }) => (
  <div className="space-y-4">
    <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
      Enter the details exactly as they appear on your Order for Recovery (TE3).
    </p>

    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
        Penalty Charge Number <span className="text-red-500">*</span>
      </label>
      <input
        type="text"
        value={data.penaltyChargeNumber || ''}
        onChange={(e) => onChange({ penaltyChargeNumber: e.target.value.toUpperCase() })}
        className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-900 ${errors.penaltyChargeNumber ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'}`}
        placeholder="e.g. DC12345678"
      />
      {errors.penaltyChargeNumber && <p className="text-red-500 text-xs mt-1">{errors.penaltyChargeNumber}</p>}
    </div>

    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
        Vehicle Registration Number <span className="text-red-500">*</span>
      </label>
      <input
        type="text"
        value={data.vehicleRegistration || ''}
        onChange={(e) => onChange({ vehicleRegistration: e.target.value.toUpperCase() })}
        className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-900 ${errors.vehicleRegistration ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'}`}
        placeholder="e.g. AB12 CDE"
      />
      {errors.vehicleRegistration && <p className="text-red-500 text-xs mt-1">{errors.vehicleRegistration}</p>}
    </div>

    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
        Applicant Name
      </label>
      <input
        type="text"
        value={data.applicantName || ''}
        onChange={(e) => onChange({ applicantName: e.target.value })}
        className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900"
        placeholder="Name as shown on TE3"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
        Location of Contravention
      </label>
      <input
        type="text"
        value={data.locationOfContravention || ''}
        onChange={(e) => onChange({ locationOfContravention: e.target.value })}
        className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900"
        placeholder="e.g. Dartford Crossing"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
        Date of Contravention <span className="text-red-500">*</span>
      </label>
      <input
        type="date"
        value={data.dateOfContravention || ''}
        onChange={(e) => onChange({ dateOfContravention: e.target.value })}
        className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-900 ${errors.dateOfContravention ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'}`}
      />
      {errors.dateOfContravention && <p className="text-red-500 text-xs mt-1">{errors.dateOfContravention}</p>}
    </div>
  </div>
);

const PersonalDetailsStep: React.FC<StepProps> = ({ data, onChange, errors }) => (
  <div className="space-y-4">
    <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
      Enter your personal details. This must match the named respondent on the Order for Recovery.
    </p>

    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
        Title <span className="text-red-500">*</span>
      </label>
      <div className="flex flex-wrap gap-2">
        {(['Mr', 'Mrs', 'Miss', 'Ms', 'Other'] as Title[]).map((title) => (
          <button
            key={title}
            type="button"
            onClick={() => onChange({ title })}
            className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
              data.title === title
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:border-primary-400'
            }`}
          >
            {title}
          </button>
        ))}
      </div>
      {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
      {data.title === 'Other' && (
        <input
          type="text"
          value={data.otherTitle || ''}
          onChange={(e) => onChange({ otherTitle: e.target.value })}
          className={`mt-2 w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-900 ${errors.otherTitle ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'}`}
          placeholder="Please specify"
        />
      )}
    </div>

    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
        Full Name (Witness/Respondent) <span className="text-red-500">*</span>
      </label>
      <input
        type="text"
        value={data.fullName || ''}
        onChange={(e) => onChange({ fullName: e.target.value })}
        className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-900 ${errors.fullName ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'}`}
        placeholder="Your full name"
      />
      {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
    </div>

    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
        Company Name <span className="text-slate-400 text-xs">(if vehicle owned by company)</span>
      </label>
      <input
        type="text"
        value={data.companyName || ''}
        onChange={(e) => onChange({ companyName: e.target.value })}
        className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900"
        placeholder="Company name (if applicable)"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
        Address <span className="text-red-500">*</span>
      </label>
      <input
        type="text"
        value={data.addressLine1 || ''}
        onChange={(e) => onChange({ addressLine1: e.target.value })}
        className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-900 mb-2 ${errors.addressLine1 ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'}`}
        placeholder="Address line 1"
      />
      <input
        type="text"
        value={data.addressLine2 || ''}
        onChange={(e) => onChange({ addressLine2: e.target.value })}
        className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 mb-2"
        placeholder="Address line 2 (optional)"
      />
      <input
        type="text"
        value={data.addressLine3 || ''}
        onChange={(e) => onChange({ addressLine3: e.target.value })}
        className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900"
        placeholder="Town/City (optional)"
      />
      {errors.addressLine1 && <p className="text-red-500 text-xs mt-1">{errors.addressLine1}</p>}
    </div>

    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
        Postcode <span className="text-red-500">*</span>
      </label>
      <input
        type="text"
        value={data.postcode || ''}
        onChange={(e) => onChange({ postcode: e.target.value.toUpperCase() })}
        className={`w-48 px-3 py-2 border rounded-lg bg-white dark:bg-slate-900 ${errors.postcode ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'}`}
        placeholder="e.g. SW1A 1AA"
      />
      {errors.postcode && <p className="text-red-500 text-xs mt-1">{errors.postcode}</p>}
    </div>
  </div>
);

const GroundsStep: React.FC<StepProps> = ({ data, onChange, errors }) => {
  const grounds = data.grounds || [];
  const toggleGround = (ground: GroundType) => {
    const newGrounds = grounds.includes(ground)
      ? grounds.filter((g) => g !== ground)
      : [...grounds, ground];
    onChange({ grounds: newGrounds });
  };

  const groundOptions: { id: GroundType; label: string; description?: string }[] = [
    { id: 'did_not_receive_pcn', label: 'I did not receive the penalty charge notice.' },
    {
      id: 'made_representations_no_reply',
      label: 'I made representations about the penalty charge to the Charging Authority concerned, within 28 days of the service of the Penalty Charge Notice, but did not receive a rejection notice.',
    },
    {
      id: 'appealed_no_response',
      label: 'I appealed to an adjudicator against the Charging Authority\'s decision to reject my representation, within 28 days of service of the Rejection notice, but have had no response to the appeal.',
    },
    {
      id: 'appealed_not_determined',
      label: 'The appeal had not been determined by the time that the charge certificate had been served.',
    },
    {
      id: 'appealed_in_favour',
      label: 'The appeal was determined in my favour.',
    },
    {
      id: 'penalty_paid',
      label: 'The penalty charge has been paid in full.',
    },
  ];

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
        Tick all boxes that apply to you. You must select at least one ground.
      </p>

      <div className="space-y-3">
        {groundOptions.map((option) => (
          <label
            key={option.id}
            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              grounds.includes(option.id)
                ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-300 dark:border-primary-700'
                : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-primary-200'
            }`}
          >
            <input
              type="checkbox"
              checked={grounds.includes(option.id)}
              onChange={() => toggleGround(option.id)}
              className="mt-1 h-4 w-4 text-primary-600 rounded border-slate-300"
            />
            <span className="text-sm text-slate-700 dark:text-slate-300">{option.label}</span>
          </label>
        ))}
      </div>
      {errors.grounds && <p className="text-red-500 text-xs mt-1">{errors.grounds}</p>}

      {grounds.includes('penalty_paid') && (
        <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
          <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
            <CreditCard size={16} />
            Payment Details
          </h4>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                Date it was paid <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={data.paymentDetails?.datePaid || ''}
                onChange={(e) =>
                  onChange({
                    paymentDetails: { ...data.paymentDetails, datePaid: e.target.value } as PaymentDetails,
                  })
                }
                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-900 text-sm ${errors.paymentDatePaid ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'}`}
              />
              {errors.paymentDatePaid && <p className="text-red-500 text-xs mt-1">{errors.paymentDatePaid}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                How was it paid <span className="text-red-500">*</span>
              </label>
              <select
                value={data.paymentDetails?.method || ''}
                onChange={(e) =>
                  onChange({
                    paymentDetails: { ...data.paymentDetails, method: e.target.value as PaymentDetails['method'] } as PaymentDetails,
                  })
                }
                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-900 text-sm ${errors.paymentMethod ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'}`}
              >
                <option value="">Select method...</option>
                <option value="cash">Cash</option>
                <option value="cheque">Cheque</option>
                <option value="debit">Debit Card</option>
                <option value="credit">Credit Card</option>
              </select>
              {errors.paymentMethod && <p className="text-red-500 text-xs mt-1">{errors.paymentMethod}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                To whom was it paid <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={data.paymentDetails?.paidTo || ''}
                onChange={(e) =>
                  onChange({
                    paymentDetails: { ...data.paymentDetails, paidTo: e.target.value } as PaymentDetails,
                  })
                }
                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-900 text-sm ${errors.paymentPaidTo ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'}`}
                placeholder="e.g. Dart Charge, Local Authority"
              />
              {errors.paymentPaidTo && <p className="text-red-500 text-xs mt-1">{errors.paymentPaidTo}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const OutOfTimeStep: React.FC<StepProps> = ({ data, onChange, errors }) => (
  <div className="space-y-4">
    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
      <p className="text-sm text-amber-800 dark:text-amber-200">
        <strong>Important:</strong> State clear reasons for not filing your Witness Statement within the time limit.
        These should NOT be the reasons why you are disputing the original penalty charge.
      </p>
    </div>

    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
        Reason(s) for filing out of time <span className="text-red-500">*</span>
      </label>
      <textarea
        value={data.outOfTimeReason || ''}
        onChange={(e) => onChange({ outOfTimeReason: e.target.value })}
        rows={8}
        className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-900 ${errors.outOfTimeReason ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'}`}
        placeholder="Explain why you could not file the Witness Statement within the time limit allowed on the Order for Recovery...

For example:
- I was hospitalised during the deadline period
- I did not receive the Order for Recovery until after the deadline
- I was out of the country and unable to respond in time"
      />
      {errors.outOfTimeReason && <p className="text-red-500 text-xs mt-1">{errors.outOfTimeReason}</p>}
      <p className="text-xs text-slate-500 mt-1">Continue on a separate sheet if necessary</p>
    </div>
  </div>
);

interface StatementOfTruthStepProps extends StepProps {
  signatureDataUrl?: string;
  onSignatureChange: (dataUrl: string | null) => void;
}

const StatementOfTruthStep: React.FC<StatementOfTruthStepProps> = ({
  data,
  onChange,
  errors,
  signatureDataUrl,
  onSignatureChange,
}) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const ctxRef = React.useRef<CanvasRenderingContext2D | null>(null);
  const drawingRef = React.useRef(false);
  const hasContentRef = React.useRef(false);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
    ctx.clearRect(0, 0, rect.width, rect.height);
    ctxRef.current = ctx;

    if (signatureDataUrl) {
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, rect.width, rect.height);
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
      };
      img.src = signatureDataUrl;
    }
  }, [signatureDataUrl]);

  const getPoint = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    event.preventDefault();
    const ctx = ctxRef.current;
    if (!ctx) return;
    drawingRef.current = true;
    hasContentRef.current = true;
    const point = getPoint(event);
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    event.preventDefault();
    const ctx = ctxRef.current;
    if (!ctx) return;
    const point = getPoint(event);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
  };

  const handlePointerEnd = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (!hasContentRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    onSignatureChange(canvas.toDataURL('image/png'));
  };

  const handleClearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    hasContentRef.current = false;
    onSignatureChange(null);
  };

  return (
    <div className="space-y-4">
    <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
      <p className="text-sm text-red-800 dark:text-red-200 font-medium">
        Proceedings for contempt of court may be brought against you if you make or cause to be made
        a false statement in an application verified by a statement of truth without an honest belief in its truth.
      </p>
    </div>

    <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={data.statementOfTruthConfirmed || false}
          onChange={(e) => onChange({ statementOfTruthConfirmed: e.target.checked })}
          className="mt-1 h-5 w-5 text-primary-600 rounded border-slate-300"
        />
        <span className="text-sm text-slate-700 dark:text-slate-300">
          <strong>(I believe) (The witness believes)</strong> that the facts stated in this statement/application are true.
        </span>
      </label>
      {errors.statementOfTruthConfirmed && (
        <p className="text-red-500 text-xs mt-2">{errors.statementOfTruthConfirmed}</p>
      )}
    </div>

    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
        Date <span className="text-red-500">*</span>
      </label>
      <input
        type="date"
        value={data.signatureDate || ''}
        onChange={(e) => onChange({ signatureDate: e.target.value })}
        className={`w-48 px-3 py-2 border rounded-lg bg-white dark:bg-slate-900 ${errors.signatureDate ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'}`}
      />
      {errors.signatureDate && <p className="text-red-500 text-xs mt-1">{errors.signatureDate}</p>}
    </div>

    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
        Print full name <span className="text-red-500">*</span>
      </label>
      <input
        type="text"
        value={data.printedName || ''}
        onChange={(e) => onChange({ printedName: e.target.value })}
        className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-900 ${errors.printedName ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'}`}
        placeholder="Your full name"
      />
      {errors.printedName && <p className="text-red-500 text-xs mt-1">{errors.printedName}</p>}
    </div>

    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
        Signature <span className="text-red-500">*</span>
      </label>
      <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-600 bg-white p-2">
        <canvas
          ref={canvasRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerLeave={handlePointerEnd}
          className="w-full h-32"
          style={{ touchAction: 'none' }}
        />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
        <button
          type="button"
          onClick={handleClearSignature}
          className="px-2 py-1 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
        >
          Clear signature
        </button>
        {signatureDataUrl ? (
          <span className="text-emerald-600 dark:text-emerald-400 font-medium">Signature captured</span>
        ) : (
          <span>Sign inside the box above</span>
        )}
      </div>
      {errors.signatureDataUrl && <p className="text-red-500 text-xs mt-1">{errors.signatureDataUrl}</p>}
    </div>

    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
        If you are signing on behalf of the witness/respondent, are you: <span className="text-red-500">*</span>
      </label>
      <div className="space-y-2">
        {[
          { id: 'witness', label: 'The witness/respondent (signing for myself)' },
          { id: 'officer', label: 'An Officer of the company' },
          { id: 'partner', label: 'A Partner of the firm' },
          { id: 'litigation_friend', label: 'A Litigation friend acting on behalf of a protected party within the meaning of the Mental Capacity Act 2005' },
        ].map((option) => (
          <label
            key={option.id}
            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              data.signingCapacity === option.id
                ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-300 dark:border-primary-700'
                : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-primary-200'
            }`}
          >
            <input
              type="radio"
              name="signingCapacity"
              checked={data.signingCapacity === option.id}
              onChange={() => onChange({ signingCapacity: option.id as SigningCapacity })}
              className="mt-1 h-4 w-4 text-primary-600 border-slate-300"
            />
            <span className="text-sm text-slate-700 dark:text-slate-300">{option.label}</span>
          </label>
        ))}
      </div>
      {errors.signingCapacity && <p className="text-red-500 text-xs mt-1">{errors.signingCapacity}</p>}
    </div>
  </div>
  );
};

// ============================================================================
// OFFICIAL PDF PREVIEW
// ============================================================================

interface PdfPreviewProps {
  formType: TeFormType;
  includeOutOfTime: boolean;
  previewUrls: { te9?: string; te7?: string };
  previewDoc: TeFormType;
  onSelectDoc: (doc: TeFormType) => void;
  onGenerate: () => void;
  onOpenInNewTab: () => void;
  previewError: string | null;
  isGenerating: boolean;
  debugEnabled: boolean;
  onToggleDebug: () => void;
}

const PdfPreviewPanel: React.FC<PdfPreviewProps> = ({
  formType,
  includeOutOfTime,
  previewUrls,
  previewDoc,
  onSelectDoc,
  onGenerate,
  onOpenInNewTab,
  previewError,
  isGenerating,
  debugEnabled,
  onToggleDebug,
}) => {
  const showTe9 = formType === 'te9';
  const showTe7 = formType === 'te7' || includeOutOfTime;
  const activeUrl = previewDoc === 'te7' ? previewUrls.te7 : previewUrls.te9;
  const hasAnyPreview = Boolean(previewUrls.te9 || previewUrls.te7);

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
      <div className="bg-slate-100 dark:bg-slate-800 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-bold text-slate-800 dark:text-slate-200">Official TEC Form Preview</h3>
            <p className="text-xs text-slate-500">Rendered on the official TE PDF template with your data overlaid.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onGenerate}
              disabled={isGenerating}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors disabled:opacity-50"
            >
              <Eye size={14} />
              {hasAnyPreview ? 'Refresh Preview' : 'Generate Preview'}
            </button>
            <button
              type="button"
              onClick={onToggleDebug}
              className={`px-2.5 py-1 text-[11px] font-medium rounded-full border ${
                debugEnabled
                  ? 'border-amber-300 bg-amber-50 text-amber-700'
                  : 'border-slate-300 text-slate-500 hover:text-slate-700 dark:border-slate-600 dark:text-slate-400 dark:hover:text-slate-200'
              }`}
            >
              Debug {debugEnabled ? 'On' : 'Off'}
            </button>
          </div>
        </div>
      </div>

      {(showTe9 && showTe7) && (
        <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 dark:border-slate-700">
          <button
            type="button"
            onClick={() => onSelectDoc('te9')}
            className={`px-3 py-1.5 text-xs font-medium rounded-full ${
              previewDoc === 'te9'
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
          >
            TE9
          </button>
          <button
            type="button"
            onClick={() => onSelectDoc('te7')}
            className={`px-3 py-1.5 text-xs font-medium rounded-full ${
              previewDoc === 'te7'
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
          >
            TE7
          </button>
        </div>
      )}

      <div className="p-4 space-y-3">
        {previewError && (
          <div className="p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs text-red-700 dark:text-red-300">
            {previewError}
          </div>
        )}

        {activeUrl ? (
          <>
            <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
              <iframe
                title="TEC official form preview"
                src={activeUrl}
                className="w-full h-[70vh] bg-white"
              />
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onOpenInNewTab}
                className="text-xs font-medium text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
              >
                Open in new tab
              </button>
            </div>
          </>
        ) : (
          <div className="border border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-4 text-center text-sm text-slate-500">
            No official preview yet. Generate a preview to review the filled form.
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN WIZARD COMPONENT
// ============================================================================

export const TeFormWizard: React.FC<TeFormWizardProps> = ({
  formType,
  includeOutOfTime = false,
  initialData,
  onSubmit,
  onCancel,
  ticket,
  organization,
  driver,
}) => {
  const today = new Date().toISOString().split('T')[0];
  const normalizeDateInput = (raw?: string) => {
    const value = String(raw || '').trim();
    if (!value) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const m = value.match(/^(\d{2})[\/-](\d{2})[\/-](\d{4})$/);
    if (m) {
      const dd = Number(m[1]);
      const mm = Number(m[2]);
      const yyyy = Number(m[3]);
      const d = new Date(Date.UTC(yyyy, mm - 1, dd, 0, 0, 0));
      if (!Number.isFinite(d.getTime())) return '';
      if (d.getUTCFullYear() !== yyyy || d.getUTCMonth() !== mm - 1 || d.getUTCDate() !== dd) return '';
      return `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
    }
    const d = new Date(value);
    if (!Number.isFinite(d.getTime())) return '';
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // Initialize form data with prefill values
  const getInitialData = (): Partial<TeFormData> => {
    return {
      penaltyChargeNumber: ticket?.pcn_number || '',
      vehicleRegistration: ticket?.vrn || '',
      applicantName: ticket?.issuing_authority || driver?.full_name || ticket?.assigned_driver_name || organization?.name || '',
      locationOfContravention: ticket?.location || '',
      dateOfContravention: normalizeDateInput(ticket?.contravention_date || ticket?.issue_date || ''),
      fullName: driver?.full_name || ticket?.assigned_driver_name || '',
      companyName: organization?.name || '',
      addressLine1: driver?.address_line1 || organization?.address_line1 || '',
      addressLine2: driver?.address_line2 || organization?.address_line2 || '',
      addressLine3: driver?.city || organization?.city || '',
      postcode: driver?.postcode || organization?.postcode || '',
      grounds: [],
      signatureDate: today,
      signingCapacity: 'witness',
      statementOfTruthConfirmed: false,
      printedName: driver?.full_name || ticket?.assigned_driver_name || '',
      ...initialData,
    };
  };

  const [formData, setFormData] = useState<Partial<TeFormData>>(getInitialData);
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [showPreview, setShowPreview] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewUrls, setPreviewUrls] = useState<{ te9?: string; te7?: string }>({});
  const [previewDoc, setPreviewDoc] = useState<TeFormType>(formType);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewDebug, setPreviewDebug] = useState(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return params.get('tecDebug') === '1' || params.get('tec_debug') === '1';
  });

  // Define steps based on form type
  const steps = useMemo(() => {
    const baseSteps = [
      { id: 'case', label: 'Case Details', icon: FileText },
      { id: 'personal', label: 'Personal Details', icon: User },
    ];

    if (formType === 'te9') {
      baseSteps.push({ id: 'grounds', label: 'Declaration Grounds', icon: Shield });
    }

    if (includeOutOfTime) {
      baseSteps.push({ id: 'outOfTime', label: 'Out of Time Reason', icon: Calendar });
    }

    baseSteps.push({ id: 'truth', label: 'Statement of Truth', icon: Check });

    return baseSteps;
  }, [formType, includeOutOfTime]);

  const handleChange = (updates: Partial<TeFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
    setPreviewError(null);
    // Clear errors for updated fields
    const updatedKeys = Object.keys(updates);
    setErrors((prev) => {
      const next = { ...prev };
      updatedKeys.forEach((key) => delete next[key]);
      return next;
    });
  };

  const handleNext = () => {
    const stepErrors = validateStep(currentStep, formData, formType, includeOutOfTime);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    setErrors({});
    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
      setErrors({});
    }
  };

  const handleSubmit = async () => {
    // Final validation
    const allErrors: ValidationErrors = {};
    for (let i = 0; i <= steps.length - 1; i++) {
      Object.assign(allErrors, validateStep(i, formData, formType, includeOutOfTime));
    }

    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      return;
    }

    setIsGenerating(true);
    try {
      const pdfBytes: { te9?: Uint8Array; te7?: Uint8Array } = {};
      const completeData = formData as TeFormData;

      // Generate TE9 (Witness Statement) PDF
      if (formType === 'te9') {
        pdfBytes.te9 = await generateTE9Pdf(completeData);
      }

      // Generate TE7 (Out of Time) PDF if needed
      if (includeOutOfTime) {
        pdfBytes.te7 = await generateTE7Pdf(completeData);
      }

      onSubmit(completeData, pdfBytes);
    } catch (err) {
      console.error('Failed to generate PDF:', err);
      setErrors({ submit: `Failed to generate PDF: ${err instanceof Error ? err.message : 'Unknown error'}. Please try again.` });
    } finally {
      setIsGenerating(false);
    }
  };

  const revokePreviewUrls = (urls: { te9?: string; te7?: string }) => {
    Object.values(urls).forEach((url) => {
      if (url) URL.revokeObjectURL(url);
    });
  };

  useEffect(() => () => revokePreviewUrls(previewUrls), [previewUrls]);

  const handlePreviewPdf = async (debugOverride?: boolean) => {
    setShowPreview(true);
    if (!formData.penaltyChargeNumber && !formData.vehicleRegistration) {
      setPreviewError('Please fill in at least the case details before previewing.');
      return;
    }

    setIsGenerating(true);
    setPreviewError(null);
    try {
      const debugEnabled = typeof debugOverride === 'boolean' ? debugOverride : previewDebug;
      const completeData = {
        ...formData,
        grounds: formData.grounds || [],
        statementOfTruthConfirmed: formData.statementOfTruthConfirmed || false,
        signingCapacity: formData.signingCapacity || 'witness',
      } as TeFormData;

      const nextPreviewUrls: { te9?: string; te7?: string } = {};

      if (formType === 'te9') {
        const te9Bytes = await generateTE9Pdf(completeData, { debug: debugEnabled });
        nextPreviewUrls.te9 = URL.createObjectURL(new Blob([te9Bytes], { type: 'application/pdf' }));
      }

      if (formType === 'te7' || includeOutOfTime) {
        const te7Bytes = await generateTE7Pdf(completeData, { debug: debugEnabled });
        nextPreviewUrls.te7 = URL.createObjectURL(new Blob([te7Bytes], { type: 'application/pdf' }));
      }

      setPreviewUrls(nextPreviewUrls);
      if (formType === 'te7') {
        setPreviewDoc('te7');
      } else if (includeOutOfTime && !nextPreviewUrls.te9 && nextPreviewUrls.te7) {
        setPreviewDoc('te7');
      } else {
        setPreviewDoc('te9');
      }
    } catch (err) {
      console.error('Failed to generate preview PDF:', err);
      setPreviewError(`Failed to generate preview: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleTogglePreviewDebug = () => {
    const next = !previewDebug;
    setPreviewDebug(next);
    if (showPreview) {
      handlePreviewPdf(next);
    }
  };

  const renderStepContent = () => {
    const stepId = steps[currentStep]?.id;
    const stepProps: StepProps = { data: formData, onChange: handleChange, errors, formType };

    switch (stepId) {
      case 'case':
        return <CaseDetailsStep {...stepProps} />;
      case 'personal':
        return <PersonalDetailsStep {...stepProps} />;
      case 'grounds':
        return <GroundsStep {...stepProps} />;
      case 'outOfTime':
        return <OutOfTimeStep {...stepProps} />;
      case 'truth':
        return (
          <StatementOfTruthStep
            {...stepProps}
            signatureDataUrl={formData.signatureDataUrl}
            onSignatureChange={(dataUrl) => handleChange({ signatureDataUrl: dataUrl || undefined })}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
        <div>
          <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200">
            {formType === 'te9' ? 'TE9 Witness Statement' : 'TE7 Out of Time Application'}
            {includeOutOfTime && formType === 'te9' && ' + TE7'}
          </h2>
          <p className="text-xs text-slate-500">Traffic Enforcement Centre Form</p>
        </div>
        <button
          type="button"
          onClick={() => {
            const next = !showPreview;
            setShowPreview(next);
            if (next && !previewUrls.te9 && !previewUrls.te7) {
              handlePreviewPdf();
            }
          }}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
        >
          <Eye size={16} />
          {showPreview ? 'Hide Preview' : 'Official Preview'}
        </button>
      </div>

      {/* Progress Steps */}
      <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 overflow-x-auto">
        <div className="flex items-center gap-2 min-w-max">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;

            return (
              <React.Fragment key={step.id}>
                {index > 0 && (
                  <div className={`w-8 h-0.5 ${isCompleted ? 'bg-primary-500' : 'bg-slate-200 dark:bg-slate-700'}`} />
                )}
                <button
                  type="button"
                  onClick={() => index < currentStep && setCurrentStep(index)}
                  disabled={index > currentStep}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                      : isCompleted
                      ? 'bg-primary-50 dark:bg-primary-900/10 text-primary-600 hover:bg-primary-100'
                      : 'text-slate-400 dark:text-slate-500'
                  }`}
                >
                  {isCompleted ? (
                    <Check size={16} className="text-primary-500" />
                  ) : (
                    <Icon size={16} />
                  )}
                  <span className="hidden sm:inline">{step.label}</span>
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className={`grid ${showPreview ? 'lg:grid-cols-2' : 'grid-cols-1'} gap-4 p-4 h-full`}>
          {/* Form */}
          <div className="space-y-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
              {renderStepContent()}
            </div>
          </div>

          {/* Preview */}
          {showPreview && (
            <div className="hidden lg:block">
              <div className="sticky top-4">
                <PdfPreviewPanel
                  formType={formType}
                  includeOutOfTime={includeOutOfTime}
                  previewUrls={previewUrls}
                  previewDoc={previewDoc}
                  onSelectDoc={setPreviewDoc}
                  onGenerate={handlePreviewPdf}
                  onOpenInNewTab={() => {
                    const activeUrl = previewDoc === 'te7' ? previewUrls.te7 : previewUrls.te9;
                    if (activeUrl) {
                      window.open(activeUrl, '_blank', 'noreferrer');
                    }
                  }}
                  previewError={previewError}
                  isGenerating={isGenerating}
                  debugEnabled={previewDebug}
                  onToggleDebug={handleTogglePreviewDebug}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
        >
          Cancel
        </button>

        <div className="flex items-center gap-2">
          {/* Preview PDF button - available after case details */}
          {currentStep >= 1 && (
            <button
              type="button"
              onClick={() => {
                void handlePreviewPdf();
              }}
              disabled={isGenerating}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700 rounded-lg transition-colors"
              title="Generate an official PDF preview to review"
            >
              <Eye size={16} />
              <span className="hidden sm:inline">Generate Official Preview</span>
            </button>
          )}

          {currentStep > 0 && (
            <button
              type="button"
              onClick={handleBack}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <ChevronLeft size={16} />
              Back
            </button>
          )}

          {currentStep < steps.length - 1 ? (
            <button
              type="button"
              onClick={handleNext}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
            >
              Next
              <ChevronRight size={16} />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isGenerating}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {isGenerating ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Send size={16} />
                  Generate &amp; Continue
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Error banner */}
      {errors.submit && (
        <div className="absolute bottom-16 left-4 right-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-sm text-red-700 dark:text-red-300">
          <AlertCircle size={16} />
          {errors.submit}
        </div>
      )}
    </div>
  );
};

export default TeFormWizard;
